import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatInput, MatInputModule } from '@angular/material/input';
import { forkJoin, map, of, Subscription, switchMap, filter, firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { toString as luceneToString } from 'lucene';

import { SoldToRelationshipGroup } from '../../../../shared/models/service';
import { EorderApiService } from '../../../services/eorder.api.service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { EorderWizardStep } from '../../../models/eorder-wizard-state';
import { FormEorder } from '../../../models/form-eorder';
import { EorderWizardService } from '../../../services/eorder-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { UserService } from '../../../../shared/services/user.service';
import { safe } from '../../../../shared/utils/safe';
import { TranslationService } from '../../../../shared/services/translation.service';
import { CodeValue } from '../../../../shared/models/boc-available-value';
import { ProductService } from '../../../services/product.api.service';
import { EorderWizardStepperService } from '../../../services/eorder-wizard-stepper.service';
import { StepProdSpecComponent } from '../step-prodspec/step-prodspec.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { SortOptionsPipe } from '../../../../shared/pipes/sorting.pipe';
import { DialogWarningComponent } from '../../../../shared/components/dialog-warning/dialog-warning.component';
import { CustomerMasterService } from '../../../../shared/services/customer-master.service';
import { LuceneService } from '../../../../shared/services/lucene.service';
import { Partner } from '../../../models/eorder';

const DEFAULT_CURRENCY_EURO = 'EUR';

@Component({
	selector: 'copo-eorder-step-header',
	templateUrl: './step-header.component.html',
	styleUrl: './step-header.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		LoadingSpinnerComponent,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		MatIconModule,

		MatSelectModule,

		MatOptionModule,
		NgFor,

		MatButtonModule,

		MatTooltipModule,
		StepProdSpecComponent,
		SortOptionsPipe
	]
})

export class StepHeaderComponent extends WizardPageContent implements OnDestroy, OnInit {
	@ViewChild('coNbrField', { read: ElementRef }) coNbrField!: ElementRef<MatInput>;

	isLoading = true;
	isSoldToAvailable = true;
	userBocData?: SoldToRelationshipGroup[];
	readonly form: FormEorder;
	bocs?: Map<string, BocClient>;
	soldToBocList?: BocClient[];
	currencyList?: CodeValue[];
	soldToList?: string[];
	sub: Subscription | undefined;
	customerChangeSubscription?: Subscription;
	periodSubscription?: Subscription;
	isCustomerNotSelected = false;
	configForBoc = { codeProp: 'businessId', valueProp: 'name' };
	configForCurrency = { codeProp: 'code', valueProp: 'description' };
	private readonly VIEW_STEP_NUM = 0;

	constructor(
		private readonly eorderWizardService: EorderWizardService,
		private readonly eorderApiService: EorderApiService,
		private readonly userService: UserService,
		private readonly customerMasterService: CustomerMasterService,
		private readonly luceneService: LuceneService,
		private readonly translationService: TranslationService,
		private readonly productService: ProductService,

		private readonly dialog: MatDialog,
		private readonly eorderStepperService: EorderWizardStepperService,
		private readonly snackBar: MatSnackBar,
	) {
		super(EorderWizardStep.ORDERINFORMATION, eorderWizardService.getState());
		this.form = this.eorderWizardService.getStateEorderForm();
		this.currencyList = this.bocAvailableValues('CURRENCY');
		this.loadParameters();
		if (this.currencyList && this.currencyList.length > 0) {
			const filteredList = this.currencyList.filter(currency =>
				currency.code === this.form.getFormCurrency().value);
			if (filteredList.length <= 0) {
				this.form.getFormCurrency().setValue(DEFAULT_CURRENCY_EURO);
			}
		} else {
			this.form.getFormCurrency().setValue('');
		}
	}

	getFormOffer() {
		return null;
	}

	ngOnInit() {
		this.eorderStepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		this.customerChangeSubscription = this.form.getFormSoldTo().valueChanges
			.pipe(
				map(() => {
					const newBoc = {
						soldTo: <string | undefined> this.form.getFormSoldTo().value,
					};
					const lastBoc = this.form.getAndUpdateLastBoc(newBoc);
					if (
						lastBoc.soldTo !== undefined
						&& newBoc.soldTo !== undefined
						&& lastBoc.soldTo !== newBoc.soldTo
					) {
						return lastBoc;
					}
					if (this.eorderApiService.isDefaultSoldTo) {
						lastBoc.soldTo = undefined;
						return lastBoc;
					}
					return undefined;
				}),
				filter(lastBoc =>
					lastBoc === undefined
					|| lastBoc.soldTo !== undefined)
			).subscribe(
				lastBoc => {
					if (this.eorderWizardService.isCartNotEmpty()) {
						const dialogRef = this.dialog.open(DialogWarningComponent, {
							data: { iconName: 'alert',
								title: 'global.warning',
								contents: [
									this.translationService.getTR('eorder.customer.change.warning'),
									this.translationService.getTR('eorder.add.item.confirm.change')
								],
								cancelButtonEnabled: true }
						});
						firstValueFrom(dialogRef.afterClosed())
							.then(result => {
								if (result) {
									this.eorderWizardService.cleanCart();
									if (<string> this.form.getFormSoldTo().value) {
										this.isCustomerNotSelected = false;
										this.updateBocBySelectedSoldTo(<string> this.form.getFormSoldTo().value);
									}
									this.productService.emitFamilyProductClickEvent(
										<string> this.form.getFormSoldTo().value
									);
								} else {
									if (lastBoc === undefined) {
										return;
									}
									this.form.getAndUpdateLastBoc(lastBoc);
									this.form.getFormSoldTo().setValue(lastBoc.soldTo, { emitEvent: false });
									this.openSnackBar(this.translationService.getTR('global.action.cancelled'));
								}
							})
							.catch((error: unknown) => {
								console.error('Unexpected Error', error);
								if (lastBoc === undefined) {
									return;
								}
								this.form.getAndUpdateLastBoc(lastBoc);
								this.form.getFormSoldTo().setValue(lastBoc.soldTo, { emitEvent: false });
								this.openSnackBar(this.translationService.getTR('global.error.unexpected'));
							});
					} else {
						if (<string> this.form.getFormSoldTo().value) {
							this.isCustomerNotSelected = false;
							this.updateBocBySelectedSoldTo(<string> this.form.getFormSoldTo().value);
						}
						this.productService.emitFamilyProductClickEvent(
							<string> this.form.getFormSoldTo().value
						);
					}
					this.getShipToList();
				}
			);
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
		if (this.customerChangeSubscription) {
			this.customerChangeSubscription.unsubscribe();
		}
		if (this.periodSubscription) {
			this.periodSubscription.unsubscribe();
		}
	}

	checkError(): boolean {
		return false;
	}

	compareWithFn(soldTo1: string, soldTo2: string) {
		return soldTo1 === soldTo2;
	}

	getShipToList() {
		const soldToBusinessId = <string> this.form.getFormSoldTo().value;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		if (soldToGroup && bocs) {
			this.eorderApiService.filteredSoldtoShiptoList = soldToGroup.shipTos.map(v => bocs.get(v)).filter(
				(v): v is BocClient => v !== undefined
			);
		}
		this.eorderWizardService.shipToList = soldToGroup && bocs
			? soldToGroup.shipTos.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	getAllPartnerInfo(shipToList?: string[]) {
		const isPartnerInfoEmpty = this.eorderWizardService.getAllPartnerInfo()?.size ?? 0;
		if (shipToList !== undefined && shipToList.length > 0 && isPartnerInfoEmpty === 0) {
			this.periodSubscription = this.customerMasterService.getPartners(
				[
					'businessPartnerNumber',
					'customerPackagingPeriodWinterFromPeriod',
					'customerPackagingPeriodWinterToPeriod',
					'customerPackagingPeriodSummerFromPeriod',
					'customerPackagingPeriodSummerToPeriod'
				],
				luceneToString(this.luceneService.concatAll('businessPartnerNumber', shipToList)),
				'EORDR'
			).subscribe({
				next: res => {
					const partnerInfo = new Map<string, Partner>();
					for (const period of res.results) {
						if (period.businessPartnerNumber) {
							partnerInfo.set(period.businessPartnerNumber, period);
						}
					}
					this.eorderWizardService.partnerInfo = partnerInfo;
				},
				error: (err: unknown) => {
					console.error('error requesting audiences', err);
				}
			});
		}
	}

	checkErrorForDraft() {
		this.form.getFormCustomerOrderNumber().markAsTouched();
		if (!this.form.getFormCustomerOrderNumber().valid) {
			this.coNbrField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormSoldTo().valid) {
			this.form.getFormSoldTo().markAsTouched();
			return true;
		}
		if (this.form.getFormCustomerOrderNumber().valid) {
			const coNbrField = this.form.getFormCustomerOrderNumber();
			if (String(coNbrField.value).trim() === '') {
				this.form.getFormCustomerOrderNumber().setValue('');
				this.coNbrField.nativeElement.focus();
				return true;
			}
		}
		return false;
	}

	private updateBocBySelectedSoldTo(soldTo: string) {
		const { bocs } = this;
		if (bocs) {
			const boc = bocs.get(soldTo);
			if (boc) {
				this.eorderWizardService.boc = boc;
			}
		}
	}

	private loadParameters() {
		this.sub = this.eorderApiService.getBocs()
			.pipe(switchMap(userBocData => {
				if (userBocData.length === 0) {
					return of(<const>[userBocData, <BocClient[]>[]]);
				}
				const businessIds = Array.from(
					new Set(userBocData.flatMap(v => [v.soldTo, ...v.shipTos, ...v.endUsers]))
				);
				const shipToList = Array.from(
					new Set(userBocData.flatMap(v => v.shipTos))
				);
				this.getAllPartnerInfo(shipToList);
				return forkJoin([
					of(userBocData),
					this.userService.findBocs(
						[
							'businessId',
							'name',
							'city',
							'address',
							'country',
							'postalCode',
							'referenceCommercialUnit',
							'referenceCommercialSubUnit',
							'referenceCommercialSubSubUnit'
						],
						businessIds.map(x => `businessId:${ x }`).join(' OR ')
					).pipe(map(v => v.results))
				]);
			})).subscribe({
				next: ([userBocData, bocs]) => {
					this.userBocData = userBocData;
					this.sub = undefined;
					this.isLoading = false;
					const bocsMap = this.bocs = new Map(bocs.map(v => [v.businessId, v]));
					this.soldToList = this.userBocData.map(v => v.soldTo);
					this.soldToBocList = this.soldToList.map(v => safe(bocsMap.get(v)));
					this.eorderApiService.filteredSoldtoShiptoList = this.soldToBocList;
					this.setupDefaultSoldTo(this.soldToBocList);
					const errorMsg = this.checkSoldToError();
					if (errorMsg !== undefined) {
						this.eorderWizardService.getState().errorInPage = true;
					} else {
						if (this.form.getFormSoldTo().value) {
							if (<string> this.form.getFormSoldTo().value) {
								this.isCustomerNotSelected = false;
							}
							this.productService.emitFamilyProductClickEvent(
								<string> this.form.getFormSoldTo().value
							);
						}
						if (!this.form.getFormSoldTo().value) {
							this.isCustomerNotSelected = true;
						}
					}
					this.getShipToList();
				},
				error: (err: unknown) => {
					console.error('error loading wizard parameters', err);
					this.sub = undefined;
					this.isLoading = false;
					this.isSoldToAvailable = false;
					this.eorderWizardService.getState().errorInPage = true;
					this.eorderWizardService.showError(
						this.translationService.getTR('eorder.wizard.parameters.error')
					);
				},
			});
	}

	private setupDefaultSoldTo(soldToBocList: BocClient[]) {
		if (soldToBocList.length === 1 && (this.form.getFormSoldTo().value === null
			|| !this.form.getFormSoldTo().value)) {
			for (const soldTo of soldToBocList) {
				this.eorderApiService.isDefaultSoldTo = true;
				this.eorderWizardService.boc = soldTo;
				this.form.getFormSoldTo().setValue(soldTo.businessId);
			}
		}
	}

	private checkSoldToError() {
		if (!this.userBocData || this.userBocData.length === 0) {
			this.isSoldToAvailable = false;
			return this.translationService.getTR('eoffer.error.nosoldto');
		}
		return undefined;
	}

	private bocAvailableValues(parameter: string) {
		return this.userService.getBocAvailableValuesBasedOnCode('EORDR', parameter);
	}

	private openSnackBar(message: string) {
		this.snackBar.open(message, undefined, { duration: 5000 });
	}
}
